import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../hooks/redux/useRedux";
import { selectCheckPayment } from "../../redux/slices/paymentSlice/PaymentSlice";
import {
  HiOutlinePencil,
  HiOutlineUser,
  HiOutlineLink,
  HiOutlineShieldCheck,
  HiOutlineMicrophone,
} from "react-icons/hi";
import CropModal from "./CropModal";
import { ModalPortal } from "../../components/ModalPortal";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";

const BASE_URL = REST_API_URL;

const USER_INFO_URL = `${BASE_URL}/auth/userinfo/`;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export function ProfileCard({ activeTab, setActiveTab }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [avatar, setAvatar] = useState("/avatar.jpg");
  const [fullName, setFullName] = useState(t("settings.loading"));
  const [cropImage, setCropImage] = useState(null);
  const [openCrop, setOpenCrop] = useState(false);
  const [showStoreSubscriptionModal, setShowStoreSubscriptionModal] =
    useState(false);

  const paymentCheck = useAppSelector(selectCheckPayment);
  const hasSubscription = !!paymentCheck?.has_active_subscription || !!paymentCheck?.stripe_subscription;
  const subscriptionData = paymentCheck;
  const subscriptionProvider = paymentCheck?.google_subscription
    ? "Google Play"
    : paymentCheck?.ios_subscription
      ? "App Store"
      : null;

  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(USER_INFO_URL, { headers: buildHeaders() });

        const data = await res.json();
        const user = data.user;

        setAvatar(user.image_url || "/avatar.jpg");
        setFullName(`${user.first_name || ""} ${user.last_name || ""}`.trim());
      } catch (e) {
        console.log("Error loading user:", e);
      }
    };

    fetchUser();
    window.addEventListener("userUpdated", fetchUser);

    return () => window.removeEventListener("userUpdated", fetchUser);
  }, []);

  const handleSelectAvatar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setCropImage(reader.result);
      setOpenCrop(true);
    };
    reader.readAsDataURL(file);
  };

  const saveCroppedAvatar = async (base64) => {
    try {
      toast.loading(t("settings.savingAvatar"));

      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");

      const u = await fetch(USER_INFO_URL, { headers: buildHeaders() });
      const uData = await u.json();

      const backendFullName =
        `${uData.user.first_name || ""} ${uData.user.last_name || ""}`.trim();
      const companyName = uData.user.company_name || "";

      const file = await base64ToFile(base64, "avatar.png");

      const formData = new FormData();
      formData.append("full_name", backendFullName);
      formData.append("company_name", companyName);
      formData.append("img_file", file);

      const formHeaders = {
        Authorization: `Bearer ${token}`,
        "User-Timezone": getUserTimeZone(),
      };

      const response = await fetch(USER_INFO_URL, {
        method: "PUT",
        headers: formHeaders,
        body: formData,
      });

      const data = await response.json().catch(() => null);

      toast.dismiss();

      if (!response.ok) {
        return toast.error(t("settings.failedSaveAvatar"));
      }

      setAvatar(base64);
      window.dispatchEvent(new Event("userUpdated"));
      toast.success(t("settings.avatarUpdated"));
    } catch (err) {
      toast.dismiss();
      toast.error(t("settings.somethingWentWrong"));
      console.error(err);
    }

    setOpenCrop(false);
  };

  const base64ToFile = async (base64, filename) => {
    const res = await fetch(base64);
    const blob = await res.blob();
    return new File([blob], filename, { type: "image/png" });
  };

  const handlePlanButtonClick = () => {
    if (subscriptionProvider && paymentCheck?.stripe_subscription === null) {
      setShowStoreSubscriptionModal(true);
      return;
    }

    navigate("/payment-plans");
  };

  const tabButton = (key) =>
    `flex items-center gap-3 p-2 rounded-xl transition-all duration-300 relative overflow-hidden cursor-pointer
     ${
       activeTab === key
         ? "bg-[#020637] text-white shadow-lg"
         : "hover:bg-[#f5f6fb] bg-white hover:shadow-md"
     }
    `;

  const iconWrapper = (key) =>
    `w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300 relative z-10
     ${
       activeTab === key
         ? "bg-white shadow-sm"
         : "bg-white border border-[#E0E0EB]"
     }
    `;

  const iconColor = (key) =>
    activeTab === key ? "text-[#020637]" : "text-[#302B2B]";

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const avatarVariants = {
    hover: { scale: 1.05, rotate: 2 },
    tap: { scale: 0.95 },
  };

  const buttonVariants = {
    hover: { scale: 1.05 },
    tap: { scale: 0.95 },
  };

  const tabVariants = {
    initial: { scale: 0.95, opacity: 0 },
    animate: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.3 },
    },
    hover: {
      scale: 1.02,
      transition: { duration: 0.2 },
    },
  };

  return (
    <>
      <motion.aside
        initial="hidden"
        animate="visible"
        variants={cardVariants}
        className="w-full lg:w-[320px] lg:shrink-0 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)] rounded-2xl p-6 flex flex-col items-center gap-6 h-auto lg:min-h-[520px] relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#020637] to-[#1e3aa0]"></div>

        <motion.div
          className="relative w-[96px] h-[96px] flex justify-end items-end isolate"
          whileHover="hover"
          variants={avatarVariants}
        >
          <img
            src={avatar}
            className="absolute inset-0 w-full h-full object-cover rounded-[68px] border-4 border-white shadow-lg"
            alt={t("settings.profileAlt")}
          />

          <motion.button
            onClick={() => fileInputRef.current.click()}
            className="relative z-[5] flex justify-center items-center w-[34px] h-[34px] bg-[#EDEEF3] rounded-xl hover:bg-[#020637] group transition-all duration-300 shadow-md"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            <HiOutlinePencil className="text-[#302B2B] w-5 h-5 group-hover:text-white transition-colors" />
          </motion.button>

          <input
            type="file"
            accept="image/*"
            className="hidden"
            ref={fileInputRef}
            onChange={handleSelectAvatar}
          />
        </motion.div>

        <div className="text-center">
          <p className="text-[20px] font-bold text-[#302B2B] mb-1">
            {fullName === "Loading..." || fullName === t("settings.loading")
              ? t("settings.loading")
              : fullName}
          </p>

          <AnimatePresence mode="wait">
            {hasSubscription ? (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 border border-green-200"
              >
                <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-green-700 font-medium">
                  {subscriptionData?.plan_name ||
                    t("settings.activeSubscription")}
                </span>
              </motion.div>
            ) : (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-200"
              >
                <div className="h-2 w-2 bg-amber-500 rounded-full"></div>
                <span className="text-xs text-amber-700 font-medium">
                  {t("settings.noSubscription")}
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3 mt-2">
          <motion.button
            onClick={() => setActiveTab("user-info")}
            className={tabButton("user-info")}
            variants={tabVariants}
            initial="initial"
            animate="animate"
            whileHover="hover"
          >
            <div className={iconWrapper("user-info")}>
              <HiOutlineUser className={`${iconColor("user-info")} w-5 h-5`} />
            </div>
            <span className="relative z-10">
              {t("settings.personalInformation")}
            </span>
          </motion.button>

          <motion.button
            onClick={() => setActiveTab("voice-cloning")}
            className={tabButton("voice-cloning")}
            variants={tabVariants}
            initial="initial"
            animate="animate"
            whileHover="hover"
          >
            <div className={iconWrapper("voice-cloning")}>
              <HiOutlineMicrophone
                className={`${iconColor("voice-cloning")} w-5 h-5`}
              />
            </div>
            <span className="relative z-10">{t("settings.voiceCloning")}</span>
          </motion.button>

          <motion.button
            onClick={() => setActiveTab("channels")}
            className={tabButton("channels")}
            variants={tabVariants}
            initial="initial"
            animate="animate"
            whileHover="hover"
          >
            <div className={iconWrapper("channels")}>
              <HiOutlineLink className={`${iconColor("channels")} w-5 h-5`} />
            </div>
            <span className="relative z-10">
              {t("settings.channelsAndApps")}
            </span>
          </motion.button>

          <motion.button
            onClick={() => setActiveTab("privacy")}
            className={tabButton("privacy")}
            variants={tabVariants}
            initial="initial"
            animate="animate"
            whileHover="hover"
          >
            <div className={iconWrapper("privacy")}>
              <HiOutlineShieldCheck
                className={`${iconColor("privacy")} w-5 h-5`}
              />
            </div>
            <span className="relative z-10">
              {t("settings.privacySettings")}
            </span>
          </motion.button>
        </div>

        <AnimatePresence>
          {paymentCheck !== null && (
            <motion.div
              initial={{ opacity: 0, y: 20, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, y: 20, height: 0 }}
              className={`w-full mt-4 p-4 rounded-xl border ${
                hasSubscription
                  ? "bg-gradient-to-r from-[#020637]/5 to-violet-50 border-[#020637]/25"
                  : "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 p-1">
                  {hasSubscription ? (
                    <>
                      <h4 className="text-sm font-semibold text-[#020637] mb-1">
                        {t("settings.activeSubscription")}
                      </h4>
                      <p className="text-xs text-[#020637] mb-3">
                        {subscriptionData?.plan_name
                          ? t("settings.planName", {
                              name: subscriptionData.plan_name,
                            })
                          : t("settings.subscriptionIsActive")}
                      </p>
                      <button
                        onClick={handlePlanButtonClick}
                        className="w-full py-2 bg-gradient-to-r from-[#020637] to-[#1e3aa0] text-white text-xs font-medium rounded-lg hover:opacity-90 transition-all duration-300 shadow-sm"
                      >
                        {t("settings.updatePlan")}
                      </button>
                    </>
                  ) : (
                    <>
                      <h4 className="text-sm font-semibold text-amber-800 mb-1">
                        {t("settings.getPremiumFeatures")}
                      </h4>
                      <p className="text-xs text-amber-700 mb-3">
                        {t("settings.premiumFeaturesDescription")}
                      </p>
                      <button
                        onClick={() => navigate("/payment-plans")}
                        className="w-full py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-medium rounded-lg hover:opacity-90 transition-all duration-300 shadow-sm"
                      >
                        {t("settings.viewPlans")}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.aside>

      <ModalPortal>
      <AnimatePresence>
        {showStoreSubscriptionModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#0F172A]/68 backdrop-blur-[2px] px-4"
            onClick={() => setShowStoreSubscriptionModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 18, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 18, scale: 0.96 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="w-full max-w-md rounded-3xl bg-white p-6 shadow-[0_24px_80px_rgba(17,24,39,0.22)]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#eef0f8] to-[#F5EEFF]">
                <HiOutlineShieldCheck className="h-6 w-6 text-[#020637]" />
              </div>

              <h3 className="text-[20px] font-semibold text-[#1F2937]">
                {t("settings.updateSubscriptionTitle")}
              </h3>
              <p className="mt-2 text-sm leading-6 text-[#6B7280]">
                {subscriptionProvider
                  ? t("settings.managedThroughStore", {
                      provider: subscriptionProvider,
                    })
                  : t("settings.managedThroughApp")}{" "}
                {t("settings.manageDescription")}
              </p>

              <div className="mt-5 rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3">
                <p className="text-sm font-medium text-[#374151]">
                  {t("settings.webUpdatesNotAvailable")}
                </p>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setShowStoreSubscriptionModal(false)}
                  className="flex-1 rounded-2xl border border-[#D1D5DB] px-4 py-3 text-sm font-medium text-[#374151] transition hover:bg-[#F9FAFB]"
                >
                  {t("settings.close")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </ModalPortal>

      <AnimatePresence>
        {openCrop && (
          <CropModal
            image={cropImage}
            onClose={() => setOpenCrop(false)}
            onSave={saveCroppedAvatar}
          />
        )}
      </AnimatePresence>
    </>
  );
}
